import React from 'react';
import Footer from './Footer/Footer';
import Header from './Header';
import { useRouter } from 'next/router';
import { excludeChatScriptPaths } from '../utils/constants';
import StickyFooter from './StickyFooter';
import Script from 'next/script';
import layoutSnapshot from '../lib/layout-snapshot.json';
import type { LayoutSnapshot } from '../lib/types/layout-snapshot';

const snapshot = layoutSnapshot as LayoutSnapshot;

interface ChatScriptProps extends React.DetailedHTMLProps<React.ScriptHTMLAttributes<HTMLScriptElement>, HTMLScriptElement> {
  chat?: string;
}

const Layout: React.FC = (props: any) => {
  const chatScriptProps: ChatScriptProps = {
    src: '//in.fw-cdn.com/30827707/437894.js',
    chat: 'true'
  };
  const router = useRouter();
  const excludeChatScriptPath = excludeChatScriptPaths.includes(router.asPath.split('/')[1]) || excludeChatScriptPaths.includes(router.asPath);
  const pathCondition =
    router.pathname.includes('/overseas-education-fair/') ||
    router.pathname.includes('/institution/') ||
    router.pathname.includes('/test-prep/') ||
    router.pathname === '/allied-services/[slug]' ||
    router.pathname.includes('/admissions-day/') ||
    router.pathname.includes('/company/') ||
    router.pathname.includes('/study-abroad-online-counselling') ||
    router.pathname.includes('/virtual-admissions-day/') ||
    router.pathname.includes('/virtual-admissions-week/') ||
    router.pathname.includes('/applications-week/') ||
    router.pathname.includes('/webinar/') ||
    router.pathname.includes('/partners/study-abroad-recruiter') ||
    router.pathname.includes('/branch/') ||
    router.pathname.includes('/landing-page/') ||
    router.pathname.includes('/franchisee/') ||
    router.pathname.includes('/recruiter/') ||
    router.pathname.includes('/thank-you') ||
    router.pathname.includes('/onboarding') ||
    router.pathname.includes('/search') ||
    router.pathname.includes('/check-email');

  const raipurThankYouPage = router.asPath.includes('/raipur-thank-you');
  const raipurBranchPage = router.asPath.includes('/branch/raipur');
  const ghaziabadThankYouPage = router.asPath.includes('/thank-you-ghaziabad');
  const ghaziabadBranchPage = router.asPath.includes('/branch/ghaziabad');
  const goaFairPage = router.asPath.includes('/overseas-education-fair/goa');

  // Consolidated GTM and Google Ads configuration
  const getTrackingScripts = () => {
    if (raipurThankYouPage || raipurBranchPage) {
      return {
        gtmId: 'GTM-TQ2D3BT',
        googleAdsId: 'AW-867475097',
        facebookPixelId: '405893726492108'
      };
    } else if (ghaziabadBranchPage || ghaziabadThankYouPage) {
      return {
        gtmId: 'GTM-PSHX9G2',
        googleAdsId: 'AW-11132067734'
      };
    } else if (goaFairPage) {
      return {
        gtmId: 'GTM-PGK2GNF'
      };
    } else {
      return {
        gtmId: 'GTM-TQ2D3BT',
        googleAdsId: 'AW-1004683207'
      };
    }
  };

  const trackingConfig = getTrackingScripts();

  return (
    <>
      {/* Consolidated GTM Script */}
      {process.env.NEXT_PUBLIC_ENV === 'production' && trackingConfig.gtmId && (
        <Script
          strategy='afterInteractive'
        >
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${trackingConfig.gtmId}');`}
        </Script>
      )}

      {/* Consolidated Google Ads Script */}
      {trackingConfig.googleAdsId && (
        <>
          <Script
            strategy='afterInteractive'
            id="gtag-aw"
            src={`https://www.googletagmanager.com/gtag/js?id=${trackingConfig.googleAdsId}`}
          />
          <Script
            strategy='afterInteractive'
            id="gtag-init-aw"
          >
            {`window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${trackingConfig.googleAdsId}');`}
          </Script>
        </>
      )}

      {/* Page-specific tracking configurations */}
      {raipurThankYouPage && (
        <>
          <Script
            strategy='afterInteractive'
          >
            {`function gtag_report_conversion(url) {
                var callback = function () {
                  if (typeof(url) != 'undefined') {
                    window.location = url;
                  }
                };
                gtag('event', 'conversion', {
                    'send_to': 'AW-867475097/-gBrCIXMvs0DEJm90p0D',
                    'event_callback': callback
                });
                return false;
            }`}
          </Script>
          <Script
            strategy='afterInteractive'
          >
            {`!function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${trackingConfig.facebookPixelId}');
            fbq('track', 'SubmitApplication');`}
          </Script>
        </>
      )}

      {raipurBranchPage && (
        <Script
          strategy='afterInteractive'
        >
          {`!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${trackingConfig.facebookPixelId}');
            fbq('track', 'PageView');`}
        </Script>
      )}

      {/* Conversion tracking for main Google Ads */}
      {trackingConfig.googleAdsId === 'AW-1004683207' && (
        <Script
          strategy='afterInteractive'
        >
          {`window.addEventListener('load', function() {
            document.querySelectorAll('[id="input_19"]').forEach(function (a) {
              a.onclick = function () {
                gtag('event', 'conversion', { 'send_to': 'AW-1004683207/3m2-CO2E-cEDEMf_iN8D' });
              };
            })
          });`}
        </Script>
      )}

      {/* GTM noscript fallbacks */}
      {process.env.NEXT_PUBLIC_ENV === 'production' && trackingConfig.gtmId && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${trackingConfig.gtmId}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
            title="Google Tag Manager"
          />
        </noscript>
      )}
      <div id='sticky-enquire-form-viewer' style={{ display: 'block', height: 0, width: '100%' }}></div>
      {!pathCondition && <Header {...snapshot.headerData} />}
      {props.children}
      {!pathCondition && <Footer {...({ footerData: snapshot.footerData } as any)} />}
      {/* {!excludeChatScriptPath && <Script {...chatScriptProps} strategy='lazyOnload' />} */}
      <StickyFooter />
    </>
  );
};

export default Layout;
